import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import reportService from "../../services/reportService";
import customerService from "../../services/customerService";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function formatCurrency(value) {
  return `₹ ${formatNumber(value)}`;
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function SellingReportPage() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filters
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    customerId: ""
  });

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.customerId) params.customerId = filters.customerId;

      const report = await reportService.getSellingReport(params);
      setRows(report.items || []);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load Selling report.");
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const data = await customerService.getActiveCustomers();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load customers", err);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalAmt = 0;
    let grandTotalAmt = 0;
    
    rows.forEach(sale => {
      totalAmt += toNumber(sale.totalAmount);
      grandTotalAmt += toNumber(sale.grandTotal);
      (sale.sellingMaterials || []).forEach(m => {
        totalQty += toNumber(m.quantity);
      });
    });

    return { totalQuantity: totalQty, totalAmount: totalAmt, grandTotal: grandTotalAmt };
  }, [rows]);

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const columns = useMemo(
    () => [
      {
        key: "expand",
        label: "",
        width: "40px",
        render: (item) => (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(item.sellingMasterId);
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <i className={`material-icons text-sm transition-transform duration-200 ${expandedRows.has(item.sellingMasterId) ? 'rotate-180' : ''}`}>
              expand_more
            </i>
          </button>
        )
      },
      {
        key: "invoiceNo",
        label: "Invoice No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">{item.invoiceNo || "-"}</span>
        ),
      },
      { 
        key: "customerName", 
        label: "Customer", 
        sortable: true,
        className: "max-w-[100px] sm:max-w-none truncate"
      },
      { 
        key: "companyName", 
        label: "Company", 
        sortable: true,
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell max-w-[120px] truncate"
      },
      { 
        key: "date", 
        label: "Date", 
        sortable: true,
        render: (item) => item.date ? new Date(item.date).toLocaleDateString('en-GB') : "-"
      },
      { 
        key: "deliveryDate", 
        label: "Delivery", 
        sortable: true,
        headerClassName: "hidden xl:table-cell",
        className: "hidden xl:table-cell",
        render: (item) => item.deliveryDate ? new Date(item.deliveryDate).toLocaleDateString('en-GB') : "-"
      },
      {
        key: "materialsCount",
        label: "Items",
        headerClassName: "text-center hidden sm:table-cell",
        className: "hidden sm:table-cell",
        render: (item) => <span className="block w-full text-center font-medium">{(item.sellingMaterials || []).length}</span>
      },
      {
        key: "totalAmount",
        label: "Base Amount",
        sortable: true,
        headerClassName: "text-right hidden sm:table-cell",
        className: "hidden sm:table-cell",
        render: (item) => <span className="block w-full text-right font-medium text-slate-700 dark:text-slate-300">{formatCurrency(item.totalAmount)}</span>,
      },
      {
        key: "grandTotal",
        label: "Grand Total",
        sortable: true,
        headerClassName: "text-right",
        render: (item) => <span className="block w-full text-right font-bold text-primary-700 dark:text-primary-300">{formatCurrency(item.grandTotal)}</span>,
      },
      {
        key: "actions",
        label: "Actions",
        render: (item) => (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <a
              href={item.generatedReport || "#"}
              target={item.generatedReport ? "_blank" : "_self"}
              rel="noreferrer"
              className={`${item.generatedReport ? "text-primary-600 hover:text-primary-700 dark:text-primary-400" : "text-slate-300 dark:text-slate-600 cursor-not-allowed"}`}
              title="View Report"
              onClick={(e) => !item.generatedReport && e.preventDefault()}
            >
              <i className="material-icons text-lg">picture_as_pdf</i>
            </a>
            <a
              href={item.invoicePdf || "#"}
              target={item.invoicePdf ? "_blank" : "_self"}
              rel="noreferrer"
              className={`${item.invoicePdf ? "text-emerald-600 hover:text-emerald-700 dark:text-emerald-400" : "text-slate-300 dark:text-slate-600 cursor-not-allowed"}`}
              title="View Invoice"
              onClick={(e) => !item.invoicePdf && e.preventDefault()}
            >
              <i className="material-icons text-lg">receipt</i>
            </a>
            <a
              href={item.attachmentPath || "#"}
              target={item.attachmentPath ? "_blank" : "_self"}
              rel="noreferrer"
              className={`${item.attachmentPath ? "text-slate-500 hover:text-slate-700 dark:text-slate-400" : "text-slate-300 dark:text-slate-600 cursor-not-allowed"}`}
              title="View Attachment"
              onClick={(e) => !item.attachmentPath && e.preventDefault()}
            >
              <i className="material-icons text-lg">attachment</i>
            </a>
          </div>
        ),
      },
    ],
    [expandedRows]
  );

  const renderExpandableContent = (sale) => (
    <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900/50 border-y border-slate-100 dark:border-slate-800">
      <div className="flex items-center gap-2 mb-3">
        <i className="material-icons text-sm text-primary-600">inventory_2</i>
        <h3 className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">Material Breakdown</h3>
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <table className="w-full min-w-[600px] text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">Yard</th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">Lot</th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">Category</th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">Description</th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400 text-right">Qty (KG)</th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400 text-right">Rate</th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400 text-right">Amount (RS)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {(sale.sellingMaterials || []).map((m, idx) => (
                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="px-3 py-2 text-[11px] font-medium text-slate-700 dark:text-slate-300">{m.yard || "-"}</td>
                  <td className="px-3 py-2 text-[11px] font-medium text-slate-700 dark:text-slate-300">{m.lot || "-"}</td>
                  <td className="px-3 py-2">
                     <span className="inline-flex items-center rounded-md bg-primary-50 dark:bg-primary-900/20 px-1.5 py-0.5 text-[9px] font-bold text-primary-700 dark:text-primary-300 uppercase tracking-tight">
                       {m.stockCategory || "-"}
                     </span>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500 dark:text-slate-400 italic">{m.description || "No description"}</td>
                  <td className="px-3 py-2 text-[11px] font-bold text-slate-900 dark:text-white text-right">{formatNumber(m.quantity)} kg</td>
                  <td className="px-3 py-2 text-[11px] font-bold text-slate-600 dark:text-slate-400 text-right">{formatCurrency(m.rate || 0)}</td>
                  <td className="px-3 py-2 text-[11px] font-bold text-primary-600 dark:text-primary-400 text-right">{formatCurrency(m.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="w-full lg:w-64 shrink-0">
          <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm h-full flex flex-col justify-between">
            <div>
              <h4 className="text-[10px] font-black uppercase text-slate-500 mb-3 border-b border-slate-100 dark:border-slate-800 pb-2">Invoice Financials</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-slate-500">Base Amount</span>
                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{formatCurrency(sale.totalAmount)}</span>
                </div>
                
                {sale.roundOff !== undefined && sale.roundOff !== 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-slate-500">Round Off</span>
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 italic">
                      {sale.roundOff > 0 ? "+" : ""}{formatCurrency(sale.roundOff)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
              <span className="text-[10px] font-black text-slate-700 dark:text-slate-300">GRAND TOTAL</span>
              <span className="text-[13px] font-black text-primary-600 dark:text-primary-400">{formatCurrency(sale.grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    setError("");
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.customerId) params.customerId = filters.customerId;

      const blob = await reportService.downloadSellingReport(format, params);
      downloadBlob(blob, `selling-report.${format}`);
    } catch (err) {
      setError(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-bold text-slate-950 dark:text-white">
            Selling Report
          </h1>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-end lg:gap-3">
          <Button variant="secondary" onClick={loadReport} isLoading={isLoading} className="col-span-2 sm:col-auto">
            <span className="flex items-center justify-center gap-1.5">
              <i className="material-icons text-sm">refresh</i>
              <span className="sm:inline">Refresh</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("xlsx")}
            isLoading={downloadingFormat === "xlsx"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-green-600">
              <i className="material-icons text-sm">grid_on</i>
              <span className="text-[10px] sm:text-xs">EXCEL</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-red-600">
              <i className="material-icons text-sm">picture_as_pdf</i>
              <span className="text-[10px] sm:text-xs">PDF</span>
            </span>
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <DatePicker
          label="From Date"
          value={filters.startDate}
          onChange={(val) => setFilters(prev => ({ ...prev, startDate: val }))}
          placeholder="Select start date"
        />
        <DatePicker
          label="To Date"
          value={filters.endDate}
          onChange={(val) => setFilters(prev => ({ ...prev, endDate: val }))}
          placeholder="Select end date"
        />
        <SearchableDropdown
          label="Customer"
          name="customerId"
          value={filters.customerId}
          onChange={handleFilterChange}
          options={customers.map(c => ({ value: c.customerId, label: c.customerName }))}
          placeholder="All Customers"
        />
        <div className="flex items-end">
          <Button variant="secondary" className="w-full h-[38px] text-xs" onClick={() => setFilters({ startDate: "", endDate: "", customerId: "" })}>
            Reset Filters
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Total Quantity
          </p>
          <p className="mt-1 sm:mt-2 text-lg sm:text-xl font-bold text-slate-950 dark:text-white">
            {formatWeight(totals.totalQuantity)}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Base Amount
          </p>
          <p className="mt-1 sm:mt-2 text-lg sm:text-xl font-bold text-slate-950 dark:text-white">
            {formatCurrency(totals.totalAmount)}
          </p>
        </div>
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 sm:p-4 shadow-sm dark:border-primary-900/50 dark:bg-primary-900/10">
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300">
            Overall Grand Total
          </p>
          <p className="mt-1 sm:mt-2 text-lg sm:text-xl font-bold text-primary-800 dark:text-primary-200">
            {formatCurrency(totals.grandTotal)}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          searchPlaceholder="Search selling records..."
          pageSize={10}
          scrollHeight="calc(100vh - 450px)"
          expandable
          expandedRows={expandedRows}
          renderExpandableContent={renderExpandableContent}
          onRowClick={(item) => toggleRow(item.sellingMasterId)}
        />
      </div>
    </div>
  );
}

export default SellingReportPage;